import type { IThenableUnknown, TConstructor, TConstructorLike, TReducer } from "./Header.ts";

import {
  isUndefined as commonIsUndefined,
  isNull as commonIsNull,
  isNullish as commonIsNullish,
  notNull as commonNotNull,
  isString as commonIsString,
  isBoolean as commonIsBoolean,
  isObject as commonIsObject,
  notNullish as commonNotNullish,
  notUndefined as commonNotUndefined,
  assertNever as commonAssertNever,
  syncCallback as commonSyncCallback,
  compareStrings as commonCompareStrings,
  deepContains as commonDeepContains,
  substringAfter as commonSubstringAfter,
  substringBefore as commonSubstringBefore,
  substringBeforeLast as commonSubstringBeforeLast,
} from "@de-xima/xima-common-js-lang";

const PatternRegExpSpecialChars = /[-/\\^$*+?.()|[\]{}]/g;

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 */
export type TMatchingKeys<Base, Condition> = {
  [Key in keyof Base]: Base[Key] extends Condition ? Key : never;
}[keyof Base];

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 */
export type TSubType<Base, Condition> = Pick<Base, TMatchingKeys<Base, Condition>>;

// Basic language utilities that provide common functionalities such as checking
// the types of values, mapping arrays etc. No DOM or PrimeFaces specific
// utilities here!

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value A value to check.
 * @returns `true` if `value` is undefined, `false` otherwise.
 */
export function isUndefined<T>(value: T | undefined): value is undefined {
  return commonIsUndefined(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value A value to check.
 * @returns `true` if `value` is `null`, `false` otherwise.
 */
export function isNull<T>(value: T | null): value is null {
  return commonIsNull(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value A value to check.
 * @returns `true` if `value` is `null` or `undefined`, `false` otherwise.
 */
export function isUndefinedOrNull<T>(value: T | undefined | null): value is undefined | null {
  return commonIsNullish(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param  value A value to check.
 * @returns `true` if `value` is `null`, `false` otherwise.
 */
export function isNotNull<T>(value: T | null): value is T {
  return commonNotNull(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value  Value to check.
 * @returns Whether the value is a string.
 */
export function isString(value: unknown): value is string {
  return commonIsString(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value  Value to check.
 * @returns Whether the value is a boolean.
 */
export function isBoolean(value: unknown): value is boolean {
  return commonIsBoolean(value);
}

/**
 * __This is broken, this function returns false even for `NaN` which is of type
 * number.__
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value  Value to check.
 * @returns Whether the value is a number and not NaN.
 */
export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !Number.isNaN(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param  value A value to check.
 * @returns `true` if `value` is `null`, `false` otherwise.
 */
export function isObject(value: unknown): value is object {
  return commonIsObject(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value A value to check.
 * @returns `true` if the value is not `undefined`; `false` otherwise.
 */
export function isNotUndefined<T>(value: T | undefined): value is T {
  return commonNotUndefined(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value A value to check.
 * @returns `true` if the value is neither `undefined` nor `null`; `false` otherwise.
 */
export function isNotUndefinedOrNull<T>(value: T | undefined | null): value is T {
  return commonNotNullish(value);
}

/**
 * Asserts that this code is never called and throws an error if it is.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Fictional value that never exists as this code cannot be called.
 */
export function assertNever(value: never): never {
  commonAssertNever(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a string.
 * @param defaultValue Value to return when not a string, default to empty
 * string.
 * @returns The value as a string. Empty string if undefined or null.
 */
export function parseString(value: unknown, defaultValue: string = ""): string {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? defaultValue : String(value);
  }
  return String(value);
}

/**
 * Converts the value to an error. Specifically, when the value is already an
 * instance of `Error`, simply returns that value. Otherwise, converts the value
 * to a string via {@link parseString} and constructs a new error with that
 * string as the error message.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse.
 * @returns The value converted to an `Error` instance.
 */
export function parseError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  return new Error(parseString(value));
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a string.
 * @param defaultValue Value to return when not a string, default to empty
 * string.
 * @returns The value as a string. Empty string if `NaN`, undefined or null.
 */
export function parseStringOptional(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : String(value);
  }
  return String(value);
}

export function parseThenable(value: unknown): IThenableUnknown<unknown> | undefined {
  const thenFn = atKey(value, "then");
  if (typeof thenFn === "function") {
    return value as IThenableUnknown<unknown>;
  }
  return undefined;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a string.
 * @param defaultValue Value to return when not a string, default to empty
 * string.
 * @returns The value as a string. Empty string if undefined or null.
 */
export function parseOptionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : String(value);
  }
  return String(value);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to an array.
 * @returns The value as an array. Array with a single element when not an array,
 * or empty array when `null` or `undefined`.
 */
export function parseArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (isNotUndefinedOrNull(value)) {
    return [value];
  }

  return [];
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to an array.
 * @param itemParser Parser that parses the array items.
 * @returns The value as an array of. Array with a single element when not an
 * array, or empty array when `null` or `undefined`.
 */
export function parseArrayAs<T>(value: unknown, itemParser: (value: unknown) => T): T[] {
  return parseArray(value).map(itemParser);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a string.
 * @param defaultValue Default when value is not parseable as a number. Defaults
 * to `NaN`.
 * @returns The value as a number. Default value if not a number.
 */
export function parseNumber(value: unknown, defaultValue: number = Number.NaN): number {
  if (typeof value === "number") {
    return value;
  }
  if (value !== undefined && value !== null) {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }

  return defaultValue;
}

/**
 * Takes a value, parses it as JSON when a string. Returns a JSON-like object
 * or an empty object when the value is neither an object nor a parseable JSON
 * string.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse.
 * @returns The parsed JSON object.
 */
export function parseJsonObject(value: unknown): Record<string, unknown> {
  let array = value;
  if (typeof array === "string") {
    try {
      array = array !== "" ? JSON.parse(array) : {};
    } catch (e) {
      console.error("Invalid JSON", e);
      array = {};
    }
  }
  if (typeof array === "object" && array !== null) {
    return array as Record<string, unknown>;
  }
  return {};
}

/**
 * Takes a value, parses it as JSON when a string. Returns a JSON-like array
 * or an empty array when the value is not an object and not a parsable
 * string.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse.
 * @returns The parsed JSON array.
 */
export function parseJsonArray(value: unknown): unknown[] {
  let array = value;
  if (typeof array === "string") {
    try {
      array = array !== "" ? JSON.parse(array) : [];
    } catch (e) {
      console.error("Invalid JSON", e);
      array = [];
    }
  }
  return Array.isArray(array) ? array : [];
}

/**
 * Takes a value, parses it as JSON when a string. Converts each item in the
 * array to the given type via the given parser. Returns a JSON-like array
 * or an empty array when the value is not an object and not a parsable string.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse.
 * @param itemParser Parser for the individual items.
 * @returns The parsed JSON array.
 */
export function parseJsonArrayAs<T>(value: unknown, itemParser: (value: unknown) => T): T[] {
  return parseJsonArray(value).map(itemParser);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse as a record.
 * @returns The record, empty if not a valid record.
 */
export function parseRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a boolean.
 * @param defaultValue Default value when not parsable as a boolean.
 * @returns The value as a boolean. Specifically:
 * - Returns `true` when `value` is (a) the boolean `true`, (b) the string
 * `"true"`, (c) a string that is neither the empty string `""`, the string
 * `"false"`, nor the string `"0"`, (d) a number that is neither `0` nor `Nan`.
 * - Returns `false` when `value` is (a) the boolean `false`, (b) the number `0`,
 * (c) the string `"false"`, or (d) the string `0`.
 * - Returns the `defaultValue` when `value` is (a) the empty string `""`, (b) the
 * number `NaN`, (c) `undefined`, or (d) `null`.
 * - When `value` is neither of type `undefined`, `null`, `number`, or `string`,
 * the value is coerced to a boolean via `!!value`.
 */
export function parseBoolean(value: unknown, defaultValue: boolean = false): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === null || value === undefined) {
    return defaultValue;
  }
  if (typeof value === "string") {
    return value === "" ? defaultValue : value !== "false" && value !== "0";
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? defaultValue : value !== 0;
  }
  return !!value;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to convert to a boolean.
 * @returns The value as a boolean. Specifically:
 * - Returns `true` when `value` is (a) the boolean `true`, (b) the string
 * `"true"`, (c) a string that is neither the empty string `""`, the string
 * `"false"`, nor the string `"0"`, (d) a number that is neither `0` nor `Nan`.
 * - Returns `false` when `value` is (a) the boolean `false`, (b) the number `0`,
 * (c) the string `"false"`, or (d) the string `0`.
 * - Returns the `undefined` when `value` is (a) the empty string `""`, (b) the
 * number `NaN`, (c) `undefined`, or (d) `null`.
 * - When `value` is neither of type `undefined`, `null`, `number`, or `string`,
 * the value is coerced to a boolean via `!!value`.
 */
export function parseBooleanOptional(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "string") {
    return value === "" ? undefined : value !== "false" && value !== "0";
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : value !== 0;
  }

  return !!value;
}

/**
 * Converts  the value to a function, if possible. If it is a function, returns
 * that function. If it is a string, evaluates that string as a JavaScript
 * expression and if it evaluates to a function, returns that. Otherwise,
 * returns `undefined`.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to parse.
 * @returns The value parsed as a function, or `undefined` when not parsable.
 */
// biome-ignore lint/complexity/noBannedTypes: This is deprecated anyways.
export function parseFunction(value: unknown): Function | undefined {
  if (typeof value === "function") {
    return value;
  }
  if (typeof value === "string") {
    try {
      const evaluated = notEmpty(value) ? PrimeFaces.csp.evalResult(value) : undefined;
      return typeof evaluated === "function" ? evaluated : undefined;
    } catch (e) {
      console.warn("Cannot evaluate as a function", value, e);
      return undefined;
    }
  }
  return undefined;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param An array.
 * @returns The last item in the array, or `undefined` if empty.
 */
export function lastItem<T>(arr: readonly T[]): T | undefined {
  return arr[arr.length - 1];
}

/**
 * Finds the index of the first item in the given array for which the predicate
 * return `true`. A polyfill that is similar to `Array.prototype.findIndex`, but
 * not guaranteed to behave the same when arguments are passed that do not match
 * the type signature.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param items Array with items to search.
 * @param predicate Predicate that checks whether an item matches.
 * @returns The index of the first item in the array for which the predicate
 * returns `true`. Otherwise, when it never returns `true` for any item, this
 * returns `-1`.
 */
export function firstIndex<T>(
  items: readonly T[],
  predicate: (item: T, index: number, array: readonly T[]) => boolean,
): number {
  let index = 0;
  for (const item of items) {
    const matches = predicate(item, index, items);
    if (matches) {
      return index;
    }
    index += 1;
  }
  return -1;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param object An object to access.
 * @param key The key to access
 * @returns The value at the given key of the object, or `undefined` when no
 * value exists, the value is `null`, or the object is not accessible (e.g.
 * when the object is a string or number).
 */
export function atKey(object: unknown, key: string | number): unknown {
  if (Array.isArray(object)) {
    const index = typeof key === "number" ? key : Number.parseInt(key, 10);
    return object[index] ?? undefined;
  }
  if (typeof object === "object" && object !== null) {
    return (object as Record<string | number, unknown>)[key] ?? undefined;
  }
  return undefined;
}

/**
 * Accesses a value at a given path of an object.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param object An object to access.
 * @param keys Path to access.
 * @returns The value at the given key, or `undefined` when the path does not
 * exist.
 */
export function atKeys(object: unknown, keys: (string | number)[]): unknown {
  let result = object;
  for (const key of keys) {
    result = atKey(result, key);
  }
  return result;
}

/**
 * Accesses the value at the given path of an object. If it is a fuction. Binds
 * it to the value of the object at the parent path and returns that bound
 * function.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param object An object to access.
 * @param keys  Path to access.
 * @param expectedArity Optional. When given, checks the arity of the function.
 * @returns The value at the given key, converted to a function and bound to the
 * value at the parent path. No-op when not a function or it does not match the
 * expected arity.
 */
export function boundFnAtKey(
  object: unknown,
  keys: (string | number)[],
  expectedArity?: number,
): (...args: unknown[]) => unknown {
  let lastObject = object;
  let prevObject = undefined as unknown;
  for (const key of keys) {
    prevObject = lastObject;
    lastObject = atKey(lastObject, key);
  }
  if (typeof lastObject === "function" && (expectedArity === undefined || expectedArity === lastObject.length)) {
    return lastObject.bind(prevObject) as (...args: unknown[]) => unknown;
  }
  return () => undefined;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string String to check.
 * @returns `true` if the string is neither undefined, nor null, nor empty.
 */
export function notEmpty(string: string | undefined | null): string is string {
  return string !== undefined && string !== null && string !== "";
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string String to check.
 * @returns `true` if the string is either undefined, null, or empty.
 */
export function isEmpty(string: string | undefined | null): string is "" | null | undefined {
  return string === undefined || string === null || string === "";
}

/**
 * Uses a default when the given string is empty.
 * @param string A string to check.
 * @param defaultValue  Default value to substitute when the given string is
 * undefined, null, or the empty string.
 * @returns Th given string, or the default when the string is empty.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 */
export function defaultIfEmpty(string: string | undefined | null, defaultValue: string): string {
  return string === undefined || string === null || string === "" ? defaultValue : string;
}

/**
 * Similar to `object instanceof proto`, but handles `undefined` and `null`
 * gracefully.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param object Object to check.
 * @param proto Prototype to check.
 * @returns `true` if object is an instance oft the given prototype, `false`
 * otherwise.
 */
export function isInstance<T>(object: unknown, proto: TConstructor<T>): object is T {
  return typeof proto === "function" && object instanceof proto;
}

/**
 * Similar to `object instanceof proto`, but handles `undefined` and `null`
 * gracefully.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param object Object to check.
 * @param proto Prototype to check.
 * @returns `true` if object is an instance oft the given prototype, `false`
 * otherwise.
 */
export function isInstanceLike<T>(object: unknown, proto: TConstructorLike<T>): object is T {
  return typeof proto === "function" && object instanceof proto;
}

/**
 * Performs a standard flat map of the given array with the mapping function.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param Array to map
 * @param mapper Mapping function.
 * @returns An array with the mapped items.
 */
export function flatMap<T, S>(
  array: readonly T[],
  mapper: (item: T, index: number, array: readonly T[]) => readonly S[],
): S[] {
  const result = [] as S[];
  for (let index = 0; index < array.length; index += 1) {
    const item = array[index];
    if (item !== undefined) {
      result.push(...mapper(item, index, array));
    }
  }
  return result;
}

/**
 * Adds all items to the given set.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param set Set to which to add items.
 * @param items Items to add to the set.
 */
export function addAllToSet<T>(set: Set<T>, items: Iterable<T>): void {
  for (const item of items) {
    set.add(item);
  }
}

/**
 * Reduces an iterable with a given collector.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param items Iterable with items to reduce.
 * @param collector Collector for the reduce operation.
 * @returns The reduced value.
 */
export function reduceIterable<T, R>(items: Iterable<T>, reducer: TReducer<T, R>): R {
  let intermediate: R = reducer[1];
  for (const item of items) {
    intermediate = reducer[0](intermediate, item);
  }
  return intermediate;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string A string to process
 * @param prefix Prefix to remove
 * @returns The given string, with the prefix removed, if it starts with the prefix.
 */
export function removePrefix(string: string | undefined, prefix: string | undefined): string {
  const realString = string ?? "";
  const realPrefix = prefix ?? "";
  if (realString.substring(0, realPrefix.length) === realPrefix) {
    return realString.substring(realPrefix.length);
  }

  return realString;
}

/**
 * Prepends the given character to the string so that
 * the string gets at least the specified number of
 * characters.
 *
 * ```javascript
 * padLeft("3", 4, "0") // => "0003"
 * padLeft("123", 2, "0") // => "123"
 * ```
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string The string to be padded.
 * @param desiredLength The length after padding.
 * @param paddingCharacter Character to be used for padding.
 * @returns The padded string.
 */
export function padLeft(string: string | undefined, desiredLength: number, paddingCharacter: string = " "): string {
  const realString = isUndefinedOrNull(string) ? "" : String(string);
  const realPaddingCharacter = String(paddingCharacter).charAt(0) ?? " ";
  const repetitions = Math.max(0, desiredLength - realString.length);
  return repeat(realPaddingCharacter, repetitions) + realString;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string String to repeat.
 * @param amount Number of times to repeat the string.
 * @returns The given string, repeated the given amount of times.
 */
export function repeat(string: string, amount: number): string {
  const result = [] as string[];
  for (let index = amount - 1; index >= 0; index -= 1) {
    result.push(string);
  }
  return result.join("");
}

/**
 * Returns the value, clamped to the interval [min, max].
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Value to clamp.
 * @param min Minimal value.
 * @param max Maximum value.
 * @returns The given value, clamped to the interval [min, max].
 */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * ```javascript
 * mod(7,4) // => 3
 * mod(-7,4) // => 1
 * mod(7,-4) // => 3
 * mod(-7,-4) // => 1
 * ```
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param n Operand on which to perform the modulo operation.
 * @param x The number by which to divide.
 * @returns The modulo n (mod x), a value always in the interval `[0,x)`
 */
export function mod(n: number, x: number): number {
  const result = n % x;
  return result < 0 ? result + x : result;
}

/**
 * Replaces all special characters so that they can be inserted into a RegExp as a literal.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param chars Characters to escape
 * @returns The given characters with those escaped that have a special meaning in a RegExp.
 */
export function escapeRegExp(chars: string): string {
  return chars.replace(PatternRegExpSpecialChars, "\\$&");
}

/**
 * Removes the given substring from the beginning of the string, when it starts
 * with that substring.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string String to process.
 * @param toRemove Substring to remove from the beginning.
 * @returns The given string with the substring removed from the beginning, or
 * the string itself when the string does not start with the substring.
 */
export function removeStart(string: string, toRemove: string): string {
  if (string.startsWith(toRemove)) {
    return string.substring(toRemove.length);
  }

  return string;
}

/**
 * Removes the given substring from the end of the string, when it end with that
 * substring.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param string String to process.
 * @param toRemove Substring to remove from the end.
 * @returns The given string with the substring removed from the end, or the
 * string itself when the string does not start with the substring.
 */
export function removeEnd(string: string, toRemove: string): string {
  if (string.endsWith(toRemove)) {
    return string.substring(0, string.length - toRemove.length);
  }

  return string;
}

/**
 * Uses `requestAnimationFrame` when available or `setTimeout` otherwise.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param fn Function to call.
 */
export function requestAnimationFrame(fn: () => void): void {
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(fn);
  } else {
    window.setTimeout(fn);
  }
}

/**
 * Wrapper for `Object.keys` with record types that returns an array of record
 * keys instead of a generic string array.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param record Record with keys to retrieve.
 * @returns The keys of the given record.
 */
export function recordKeys<K extends PropertyKey>(record: Record<K, unknown>): K[] {
  return Object.keys(record) as K[];
}

/**
 * Returns all entries in a record. Like `Object.entries`.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param record  A record over which to iterate.
 * @returns Each entry of the given record.
 */
export function recordEntries<K extends PropertyKey, V>(record: Record<K, V>): [value: Record<K, V>[K], key: K][] {
  const entries: [value: Record<K, V>[K], key: K][] = [];
  for (const key of Object.keys(record) as K[]) {
    const value = record[key];
    entries.push([value, key]);
  }
  return entries;
}

/**
 * Returns all entries in a record. Like `Object.entries`.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param record  A record over which to iterate.
 * @returns Each entry of the given record.
 */
export function recordValues<K extends PropertyKey, V>(record: Record<K, V>): V[] {
  const values: V[] = [];
  for (const key of recordKeys(record)) {
    values.push(record[key]);
  }
  return values;
}

/**
 * Creates a new record by applying a mapping function to each key-value pair
 * of the given record.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @template K Type of the record keys.
 * @template V Type of the input record values.
 * @template V2 Type of the output record values.
 * @param record Record to map.
 * @param  valueMapper Mapping function.
 * @returns The mapped record.
 */
export function mapRecordValues<K extends PropertyKey, V, V2>(
  record: Record<K, V>,
  valueMapper: (value: V, key: K) => V2,
): Record<K, V2> {
  const result: Partial<Record<K, V2>> = {};
  for (const key of recordKeys(record)) {
    result[key] = valueMapper(record[key], key);
  }
  // We made sure we assigned a value for each key in the loop above
  return result as Record<K, V2>;
}

/**
 * Takes each value of the given record, maps it via the mapping function and
 * returns an array with all mapped values.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @template K Type of the record keys.
 * @template V1 Type of the input record values.
 * @template V2 Type of the output record values.
 * @param record Record to map.
 * @param valueMapper Mapping function.
 * @returns The mapped record values.
 */
export function mapRecordValuesToArray<K extends PropertyKey, V1, V2>(
  record: Record<K, V1>,
  valueMapper: (value: V1) => V2,
): V2[] {
  const array: V2[] = [];
  for (const key of recordKeys(record)) {
    array.push(valueMapper(record[key]));
  }
  return array;
}

/**
 * Checks whether any items matches a predicate. Arrays and objects are iterated, everything else is passed
 * directly to the callback function.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param value Items to check whether any matches the callback function.
 * @param predicate Test to apply to each item.
 * @returns `true` iff the callback function returns true at least once for any of the given items.
 */
export function deepAny(
  value: unknown,
  predicate: (value: unknown, key: string | number | undefined) => boolean,
): boolean {
  return commonDeepContains(value, predicate);
}

/**
 * Takes a function that produces a promise and returns a function that returns
 * nothing by ignoring the result of the promise. When the promise is rejected,
 * an error is logged.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param fn Function that returns a promise.
 * @returns A function that calls the given function and returns nothing.
 */
export function swallowPromiseResult<Args extends unknown[]>(
  fn: (...args: Args) => Promise<unknown>,
): (...args: Args) => void {
  return commonSyncCallback(fn);
}

/**
 * Compares two strings lexicographically (in Unicode order). This is
 * case-sensitive.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param lhs First string to compare.
 * @param rhs Second string to compare.
 * @returns `-1` if `lhs < rhs`, `+1` if `lhs > rhs`, `0` if `lhs === rhs`.
 */
export function compareStrings(lhs: string, rhs: string): number {
  return commonCompareStrings(lhs, rhs);
}

/**
 * Evaluates the given code that must be a valid EcmaScript statement or
 * statement list. The code is evaluated inside an anonymous function scope. The
 * given arguments and this argument are made available to the code.
 * @deprecated Use module `@de-xima/xima-common-js-lang`. Will be removed in version 8.
 * @param globalEvaluator Global evaluator to use, such as jQuery's `globalEval`
 * or PrimeFaces' `csp.globalEval`.
 * @param code Code to evaluate.
 * @param args Arguments to pass on to the code.
 * @param thisArg Value for the this argument for the code.
 */
export function globalEvalVoid(
  globalEvaluator: (code: string) => void,
  code: string,
  args: Record<string, unknown>,
  thisArg: unknown = globalThis,
): void {
  const entries = recordEntries(args);
  const names = entries.map((entry) => entry[1]);
  const values = entries.map((entry) => entry[0]);
  const scoped = `(function(${names.join(
    ",",
  )}){try{\n${code}\n;}catch(e){(globalThis||window).__GLOBAL_EVAL__.error=e;}}).apply((globalThis||window).__GLOBAL_EVAL__.thisArg,(globalThis||window).__GLOBAL_EVAL__.args)`;
  let error: unknown;
  try {
    parseRecord(globalThis ?? window).__GLOBAL_EVAL__ = {
      args: values,
      thisArg: thisArg,
    };
    globalEvaluator(scoped);
  } finally {
    error = parseRecord(parseRecord(globalThis ?? window).__GLOBAL_EVAL__).error;
    delete parseRecord(globalThis ?? window).__GLOBAL_EVAL__;
  }
  if (error !== undefined) {
    throw parseError(error);
  }
}

export function deleteProperty<T, K extends keyof T>(object: T, key: K): void {
  delete object[key];
}

/**
 * Returns the substring before the last occurrence of the separator.
 * @param string String to process.
 * @param separator Separator to search for.
 * @returns The substring before the last occurrence of the separator.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 */
export function substringBeforeLast(string: string, separator: string): string {
  return commonSubstringBeforeLast(string, separator);
}

/**
 * Returns the substring after the first occurrence of the separator.
 * @param string String to process.
 * @param separator Separator to search for.
 * @returns The substring after the first occurrence of the separator.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 */
export function substringBefore(string: string, separator: string): string {
  return commonSubstringBefore(string, separator);
}

/**
 * Returns the substring after the last occurrence of the separator.
 * @param string String to process.
 * @param separator Separator to search for.
 * @returns The substring after the last occurrence of the separator.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 */
export function substringAfter(string: string, separator: string): string {
  return commonSubstringAfter(string, separator);
}

export function bindThis<T, A extends unknown[], R>(fn: (thisArg: T, ...args: A) => R): (this: T, ...args: A) => R {
  return function (...args) {
    return fn(this, ...args);
  };
}

export * as Reducers from "./Reducers.ts";
